import {
  ALERT_STATE_UI,
  ALERT_VIEW_LABELS,
  ALERT_VIEWS,
  SEVERITY_UI,
  ageLabel,
} from '@baseline/shared';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { filterQuery, useAlerts, useAssemblies } from '../api/queries.js';
import { isFiltered, readFilter } from '../app/filters.js';
import { EmptyState } from '../components/EmptyState.js';
import { PageError } from '../components/PageStatus.js';
import { SeverityPill, StatusPill } from '../components/Pills.js';

import type { AlertFilter } from '../api/queries.js';
import type { AlertState, AlertView, Severity } from '@baseline/shared';

// Prototype `vAlerts` (plan Phase 11): filter chips, the table, j/k/↑/↓ and Enter.

export default function AlertsPage() {
  const [params, setParams] = useSearchParams();
  const filter = readFilter(params);
  const navigate = useNavigate();
  const { data, error } = useAlerts(filter);
  const assemblies = useAssemblies();
  const [cur, setCur] = useState(0);
  const body = useRef<HTMLTableSectionElement>(null);

  const setFilter = (next: AlertFilter) => {
    setCur(0);
    setParams(new URLSearchParams(filterQuery(next).slice(1)));
  };

  const list = data?.alerts ?? [];
  const at = Math.min(cur, Math.max(0, list.length - 1));
  const open = (id: string) => void navigate(`/alerts/${id}${filterQuery(filter)}`);

  // j/k/↑/↓ move the current row, Enter opens it (prototype keyboard handler).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement;
      if (el && /INPUT|TEXTAREA|SELECT/.test(el.tagName)) return;
      if (e.metaKey || e.ctrlKey || e.altKey || document.getElementById('pal-q')) return;
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault();
        setCur(Math.min(list.length - 1, at + 1));
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault();
        setCur(Math.max(0, at - 1));
      } else if (e.key === 'Enter' && list[at] && !(el instanceof HTMLButtonElement)) {
        e.preventDefault();
        open(list[at].id);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  useEffect(() => {
    body.current?.querySelector('tr.cur')?.scrollIntoView?.({ block: 'nearest' });
  }, [at]);

  if (error) return <PageError error={error} />;
  const counts = data?.counts;

  return (
    <div className="pg">
      <div className="ph">
        <div>
          <h1>Alerts</h1>
          <p className="ps">
            Differences between design (PDM) and purchasing (ERP) that need a decision.
          </p>
        </div>
      </div>
      <div className="fbar">
        <select
          className={`chip${filter.view !== 'all' ? ' on' : ''}`}
          id="f-tab"
          aria-label="Filter by view"
          value={filter.view}
          onChange={(e) => setFilter({ ...filter, view: e.target.value as AlertView })}
        >
          {ALERT_VIEWS.map((v) => (
            <option key={v} value={v}>
              View: {ALERT_VIEW_LABELS[v]} ({counts?.[v] ?? 0})
            </option>
          ))}
        </select>
        <select
          className={`chip${filter.severity !== null ? ' on' : ''}`}
          id="f-sev"
          aria-label="Filter by severity"
          value={filter.severity ?? ''}
          onChange={(e) =>
            setFilter({
              ...filter,
              severity: e.target.value === '' ? null : (Number(e.target.value) as Severity),
            })
          }
        >
          <option value="">Severity: any</option>
          {([2, 1, 0] as const).map((l) => (
            <option key={l} value={l}>
              Severity: {SEVERITY_UI[l].label}
            </option>
          ))}
        </select>
        <select
          className={`chip${filter.assemblyId ? ' on' : ''}`}
          id="f-asm"
          aria-label="Filter by assembly"
          value={filter.assemblyId ?? ''}
          onChange={(e) => setFilter({ ...filter, assemblyId: e.target.value || null })}
        >
          <option value="">Assembly: any</option>
          {(assemblies.data?.assemblies ?? []).map((a) => (
            <option key={a.id} value={a.id}>
              Assembly: {a.name}
            </option>
          ))}
        </select>
        {filter.state ? (
          <span className="chip on">
            Status: {ALERT_STATE_UI[filter.state as AlertState].label}
          </span>
        ) : null}
        {isFiltered(filter) ? (
          <button
            className="lnk"
            type="button"
            onClick={() =>
              setFilter({ view: 'all', severity: null, assemblyId: null, state: null })
            }
          >
            Clear filters
          </button>
        ) : null}
        <span className="cnt">
          {list.length} result{list.length === 1 ? '' : 's'}
        </span>
      </div>
      {!data ? null : !list.length ? (
        <EmptyState
          title="No alerts match these filters."
          sub="Clear a filter or pick another view."
        />
      ) : (
        <div className="card">
          <table className="tbl">
            <thead>
              <tr>
                <th>Severity</th>
                <th>Alert</th>
                <th className="hm">Assembly</th>
                <th>Status</th>
                <th className="num hm">Age</th>
              </tr>
            </thead>
            <tbody ref={body}>
              {list.map((a, i) => {
                const age = ageLabel(a.openedAt);
                return (
                  <tr
                    key={a.id}
                    tabIndex={0}
                    className={i === at ? 'cur' : ''}
                    onClick={() => open(a.id)}
                    onFocus={() => setCur(i)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        open(a.id);
                      }
                    }}
                  >
                    <td>
                      <SeverityPill level={a.severity} />
                    </td>
                    <td>
                      <div className="cell-t">{a.title}</div>
                      <div className="cell-s">
                        <span className="mono-s">{a.code}</span> · {a.summary}
                        <span className="mo">
                          {' '}
                          · {a.assemblyName} · {age}
                        </span>
                      </div>
                    </td>
                    <td className="hm">{a.assemblyName}</td>
                    <td>
                      <StatusPill state={a.state} />
                    </td>
                    <td className="num hm cell-s">{age}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
